import { useMemo, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import AttendanceMonthCalendar from "../../components/attendance/AttendanceMonthCalendar";
import AttendanceLegend from "../../components/attendance/AttendanceLegend";
import { useApi } from "../../hooks/useApi";
import {
  myAttendanceToday,
  myAttendanceMonth,
  myHolidays,
  punchIn,
  punchOut,
} from "../../api/attendanceService";
import { minutesLabel, captureLocation } from "../../utils/attendance";
import { nowIst, formatDateTime } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";

/**
 * Self-service attendance: a big check-in/out button driven by the live
 * "today" endpoint, today's slots, and a colour-coded month calendar.
 * Location is requested with a short timeout on every punch — denial or
 * timeout never blocks the punch, the slot just tags as "unknown".
 */
export default function MyAttendancePage() {
  const [month, setMonth] = useState(nowIst().format("YYYY-MM"));
  const [punching, setPunching] = useState(false);

  const today = useApi(() => myAttendanceToday());
  const monthApi = useApi(() => myAttendanceMonth(month), [month]);
  const holidays = useApi(() => myHolidays(Number(month.slice(0, 4))), [month.slice(0, 4)]);

  const t = today.data?.data;
  const days = useMemo(() => {
    const list = monthApi.data?.data?.days || [];
    const holidayNames = new Map((holidays.data?.data || []).map((h) => [String(h.date).slice(0, 10), h.name]));
    return list.map((d) => (holidayNames.has(d.work_date) ? { ...d, holiday_name: holidayNames.get(d.work_date) } : d));
  }, [monthApi.data, holidays.data]);

  const doPunch = async (checkedIn) => {
    setPunching(true);
    try {
      const coords = await captureLocation();
      if (checkedIn) {
        await punchOut(coords);
        showToast("success", "Checked out");
      } else {
        await punchIn(coords);
        showToast("success", "Checked in");
      }
      today.reload();
      monthApi.reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setPunching(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">My Attendance</h1>
          <div className="page-sub">Check in when you start and out when you stop — multiple sessions a day are fine.</div>
        </div>
      </div>

      {/* Punch widget */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body">
          {today.loading ? (
            <LoadingBlock rows={2} />
          ) : (
            <div className="row gap-md" style={{ flexWrap: "wrap", alignItems: "center" }}>
              <button
                className={`btn ${t?.checked_in ? "btn-secondary" : "btn-primary"}`}
                style={{ padding: "14px 26px", fontSize: 15 }}
                disabled={punching}
                onClick={() => doPunch(t?.checked_in)}
              >
                <Icon name={t?.checked_in ? "log-out" : "check-circle"} size={16} />
                {punching ? "…" : t?.checked_in ? "Check out" : "Check in"}
              </button>

              <div className="col" style={{ gap: 2, minWidth: 180 }}>
                <div className="t-primary fw-500 text-sm">
                  {t?.checked_in ? "You're checked in" : "You're not checked in"}
                </div>
                <div className="t-muted text-xs">
                  Worked today: {minutesLabel(t?.summary?.worked_minutes || 0)}
                  {t?.summary?.late ? ` · late ${t.summary.late_minutes}m` : ""}
                </div>
                {t?.shift && (
                  <div className="t-muted text-xs">
                    Shift: {t.shift.name} · {String(t.shift.start_time).slice(0, 5)}–{String(t.shift.end_time).slice(0, 5)}
                  </div>
                )}
              </div>

              {(t?.logs || []).length > 0 && (
                <div className="col" style={{ gap: 4, flex: 1, minWidth: 220 }}>
                  <div className="t-muted text-xs fw-500">Today's sessions</div>
                  {t.logs.map((l) => (
                    <div key={l.id} className="row gap-sm text-xs" style={{ alignItems: "center" }}>
                      <Icon name="clock" size={12} />
                      <span>{formatDateTime(l.check_in_at).split("·")[1]?.trim()}</span>
                      <span className="t-muted">→</span>
                      <span>{l.check_out_at ? formatDateTime(l.check_out_at).split("·")[1]?.trim() : "…"}</span>
                      {geoBadge(l.in_geofence_status)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Month calendar */}
      <div className="card">
        <div className="card-body">
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div className="t-primary fw-500 text-sm">Month view</div>
            <input
              type="month"
              className="input"
              style={{ width: 170 }}
              value={month}
              onChange={(e) => e.target.value && setMonth(e.target.value)}
            />
          </div>
          {monthApi.loading ? (
            <LoadingBlock rows={4} height={60} />
          ) : (
            <>
              <AttendanceMonthCalendar month={month} days={days} />
              <AttendanceLegend />
            </>
          )}
        </div>
      </div>
    </>
  );
}

function geoBadge(status) {
  if (status === "inside") return <span className="badge badge-success">inside</span>;
  if (status === "outside") return <span className="badge badge-danger">outside</span>;
  return null;
}
